import { useCallback, useEffect, useRef, useState } from "react"
import { useMediaQuery, useTheme } from "@mui/material"

import {
  CLICK_DEADZONE_PX,
  EDGE_RESISTANCE,
  SUPPRESS_CLICK_AFTER_SWIPE_MS,
  TRANSITION_DURATION,
  VERTICAL_THRESHOLD,
} from "./style"

const SWIPE_THRESHOLD_RATIO = 0.15

type UseImageGalleryModalLogicParams = {
  open: boolean
  onClose: () => void
  images: string[]
  initialIndex: number
}

export const useImageGalleryModalLogic = ({
  open,
  onClose,
  images,
  initialIndex,
}: UseImageGalleryModalLogicParams) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [currentIndex, setCurrentIndex] = useState(0)

  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const currentIndexRef = useRef(currentIndex)

  const startX = useRef(0)
  const startY = useRef(0)
  const currentTranslate = useRef(0)
  const prevTranslate = useRef(0)
  const isDragging = useRef(false)
  const dragDirection = useRef<"horizontal" | "vertical" | null>(null)

  const pointerDownPos = useRef<{ x: number; y: number } | null>(null)
  const suppressClickUntil = useRef(0)

  const safeInitialIndex =
    images?.length > 0
      ? Math.min(Math.max(0, initialIndex), images.length - 1)
      : 0

  const getSlideWidth = () =>
    viewportRef.current?.clientWidth ?? window.innerWidth

  const setPositionByIndex = useCallback((index: number) => {
    if (!trackRef.current) return

    const width = viewportRef.current?.clientWidth ?? window.innerWidth
    const pos = -index * width

    trackRef.current.style.transition = "none"
    trackRef.current.style.transform = `translateX(${pos}px)`

    prevTranslate.current = pos
    currentTranslate.current = pos
  }, [])

  const moveToIndex = useCallback(
    (index: number) => {
      if (!trackRef.current) return

      const clamped = Math.min(Math.max(0, index), images.length - 1)
      const width = viewportRef.current?.clientWidth ?? window.innerWidth

      trackRef.current.style.transition = TRANSITION_DURATION
      const pos = -clamped * width
      trackRef.current.style.transform = `translateX(${pos}px)`

      prevTranslate.current = pos
      currentTranslate.current = pos
      setCurrentIndex(clamped)
    },
    [images.length]
  )

  useEffect(() => {
    currentIndexRef.current = currentIndex
  }, [currentIndex])

  useEffect(() => {
    if (open) {
      setCurrentIndex(safeInitialIndex)
      suppressClickUntil.current = 0
      pointerDownPos.current = null
      const id = requestAnimationFrame(() => {
        setPositionByIndex(safeInitialIndex)
      })
      return () => cancelAnimationFrame(id)
    }
  }, [open, safeInitialIndex, setPositionByIndex])

  useEffect(() => {
    if (!open) return

    const updatePosition = () => {
      requestAnimationFrame(() => setPositionByIndex(currentIndexRef.current))
    }

    window.addEventListener("resize", updatePosition)

    const el = viewportRef.current
    if (el) {
      const observer = new ResizeObserver(updatePosition)
      observer.observe(el)
      return () => {
        observer.disconnect()
        window.removeEventListener("resize", updatePosition)
      }
    }

    return () => window.removeEventListener("resize", updatePosition)
  }, [open, setPositionByIndex])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowLeft" && currentIndex > 0) moveToIndex(currentIndex - 1)
      if (e.key === "ArrowRight" && currentIndex < images.length - 1)
        moveToIndex(currentIndex + 1)
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, currentIndex, images.length, onClose, moveToIndex])

  const closeFromSwipe = useCallback(() => {
    suppressClickUntil.current = Date.now() + SUPPRESS_CLICK_AFTER_SWIPE_MS
    onClose()
  }, [onClose])

  const onTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true
    dragDirection.current = null
    startX.current = e.touches[0].clientX
    startY.current = e.touches[0].clientY
    pointerDownPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }

    if (trackRef.current) {
      trackRef.current.style.transition = "none"
    }
  }

  const isSingle = images.length <= 1

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging.current || !trackRef.current) return

      const moveX = e.touches[0].clientX - startX.current
      const moveY = e.touches[0].clientY - startY.current

      if (!dragDirection.current) {
        dragDirection.current =
          Math.abs(moveX) > Math.abs(moveY) ? "horizontal" : "vertical"
      }

      if (dragDirection.current === "horizontal") {
        // Single-image preview: no gallery browse.
        if (isSingle) return

        let xPos = prevTranslate.current + moveX
        if (
          (currentIndex === 0 && moveX > 0) ||
          (currentIndex === images.length - 1 && moveX < 0)
        ) {
          xPos = prevTranslate.current + moveX * EDGE_RESISTANCE
        }

        currentTranslate.current = xPos
        trackRef.current.style.transform = `translateX(${xPos}px)`
      } else {
        trackRef.current.style.transform = `translateX(${prevTranslate.current}px) translateY(${moveY}px)`
      }
    },
    [currentIndex, images.length, isSingle]
  )

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging.current) return

      isDragging.current = false

      const endY = e.changedTouches[0].clientY
      const diffY = endY - startY.current

      if (dragDirection.current === "vertical" && Math.abs(diffY) > VERTICAL_THRESHOLD) {
        closeFromSwipe()
        return
      }

      if (dragDirection.current === "horizontal" && !isSingle) {
        const movedBy = currentTranslate.current - prevTranslate.current
        const threshold = getSlideWidth() * SWIPE_THRESHOLD_RATIO

        if (movedBy < -threshold && currentIndex < images.length - 1) {
          moveToIndex(currentIndex + 1)
        } else if (movedBy > threshold && currentIndex > 0) {
          moveToIndex(currentIndex - 1)
        } else {
          moveToIndex(currentIndex)
        }
        return
      }

      moveToIndex(currentIndex)
    },
    [closeFromSwipe, currentIndex, images.length, isSingle, moveToIndex]
  )

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return
    pointerDownPos.current = { x: e.clientX, y: e.clientY }
  }, [])

  const onTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (Date.now() < suppressClickUntil.current) return

      const start = pointerDownPos.current
      pointerDownPos.current = null
      if (start) {
        const dx = Math.abs(e.clientX - start.x)
        const dy = Math.abs(e.clientY - start.y)
        if (dx > CLICK_DEADZONE_PX || dy > CLICK_DEADZONE_PX) return
      }

      const target = e.target as HTMLElement | null
      if (target?.closest?.("[data-gallery-image]")) return

      onClose()
    },
    [onClose]
  )

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) moveToIndex(currentIndex - 1)
  }, [currentIndex, moveToIndex])

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) moveToIndex(currentIndex + 1)
  }, [currentIndex, images.length, moveToIndex])

  return {
    isMobile,
    isSingle,
    currentIndex,
    viewportRef,
    trackRef,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onPointerDown,
    onTrackClick,
    handlePrev,
    handleNext,
  }
}
